import React from 'react';

const PropertyCard = ({ property, onViewDetails, onBook, onDebug, isAuthenticated = false }) => {
  // Generate property-specific image based on title and category
  const getPropertyImage = () => {
    const title = property.title.toLowerCase();
    const category = property.category;
    
    // Determine image type based on property title keywords
    if (title.includes('villa')) {
      return `https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&h=300&fit=crop&auto=format`;
    } else if (title.includes('apartment') || title.includes('flat')) {
      return `https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&h=300&fit=crop&auto=format`;
    } else if (title.includes('cottage') || title.includes('house')) {
      return `https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&h=300&fit=crop&auto=format`;
    } else if (title.includes('lodge') || title.includes('residence')) {
      return `https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&h=300&fit=crop&auto=format`;
    } else if (title.includes('loft')) {
      return `https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400&h=300&fit=crop&auto=format`;
    } else if (title.includes('penthouse')) {
      return `https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400&h=300&fit=crop&auto=format`;
    } else if (title.includes('studio')) {
      return `https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400&h=300&fit=crop&auto=format`;
    } else if (title.includes('townhouse')) {
      return `https://images.unsplash.com/photo-1571939228382-b2f2b585ce15?w=400&h=300&fit=crop&auto=format`;
    } else if (category === 'mid_range') {
      return `https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=400&h=300&fit=crop&auto=format`;
    } else {
      return `https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&h=300&fit=crop&auto=format`;
    }
  };

  const placeholderImage = getPropertyImage();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getAmenityIcon = (amenity) => {
    const iconMap = {
      'wifi': 'fas fa-wifi',
      'free_parking': 'fas fa-parking',
      'gym': 'fas fa-dumbbell',
      'swimming_pool': 'fas fa-swimmer',
      'laundry_facilities': 'fas fa-tshirt',
      '24_hour_security': 'fas fa-shield-alt',
      'dedicated_study_area': 'fas fa-book',
      'air_conditioning': 'fas fa-snowflake',
      'balcony': 'fas fa-building',
      'communal_kitchen': 'fas fa-utensils',
      'garden_space': 'fas fa-leaf',
      'backup_power': 'fas fa-bolt'
    };
    return iconMap[amenity] || 'fas fa-check-circle';
  };

  return (
    <div className="card h-100 shadow-sm hover-card">
      {/* Property Image */}
      <div className="position-relative">
        <img 
          src={placeholderImage} 
          className="card-img-top" 
          alt={property.title}
          style={{ height: '200px', objectFit: 'cover' }}
        />
        <div className="position-absolute top-0 end-0 m-2">
          <span className={`badge ${property.category === 'budget' ? 'bg-success' : 'bg-primary'}`}>
            {property.category === 'budget' ? 'Budget' : 'Mid-Range'}
          </span>
        </div>
        {property.accommodation_details.available_rooms <= 2 && (
          <div className="position-absolute bottom-0 start-0 m-2">
            <span className="badge bg-warning text-dark">
              Only {property.accommodation_details.available_rooms} left!
            </span>
          </div>
        )}
      </div>

      <div className="card-body d-flex flex-column">
        {/* Title and Location */}
        <h5 className="card-title mb-2">{property.title}</h5>
        <p className="text-muted mb-2">
          <i className="fas fa-map-marker-alt me-1"></i>
          {property.address.suburb}, {property.address.city}
        </p>

        {/* Distance */}
        <p className="text-muted small mb-2">
          <i className="fas fa-route me-1"></i>
          {property.distance_from_campus} km from campus
        </p>

        {/* Description */}
        <p className="card-text text-muted small mb-3">
          {property.description}
        </p>

        {/* Accommodation Details */}
        <div className="mb-3">
          <div className="row g-2 text-small">
            <div className="col-6">
              <span className="badge bg-light text-dark">
                <i className="fas fa-bed me-1"></i>
                {property.accommodation_details.room_types.join(', ')} rooms
              </span>
            </div>
            <div className="col-6">
              <span className="badge bg-light text-dark">
                <i className="fas fa-bath me-1"></i>
                {property.accommodation_details.bathroom_type} bathroom
              </span>
            </div>
            <div className="col-6">
              <span className="badge bg-light text-dark">
                <i className="fas fa-users me-1"></i>
                {property.accommodation_details.gender_preference.replace('_', ' ')}
              </span>
            </div>
            <div className="col-6">
              <span className="badge bg-light text-dark">
                <i className="fas fa-home me-1"></i>
                {property.accommodation_details.available_rooms}/{property.accommodation_details.total_rooms} available
              </span>
            </div>
          </div>
        </div>

        {/* Top Amenities */}
        <div className="mb-3">
          <div className="d-flex flex-wrap gap-1">
            {property.amenities.slice(0, 4).map((amenity, index) => (
              <span key={index} className="text-success small">
                <i className={`${getAmenityIcon(amenity)} me-1`}></i>
              </span>
            ))}
            {property.amenities.length > 4 && (
              <small className="text-muted">+{property.amenities.length - 4} more</small>
            )}
          </div>
        </div>

        {/* Price and Booking */}
        <div className="mt-auto">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0 text-primary">{formatPrice(property.price_per_month)}</h5>
              <small className="text-muted">per month</small>
            </div>
            <div>
              <div className="mb-2">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => onViewDetails && onViewDetails(property)}
                >
                  View Details
                </button>

                {isAuthenticated && (
                  <button
                    className="btn btn-success btn-sm ms-2"
                    onClick={() => onBook && onBook(property)}
                  >
                    Book Now
                  </button>
                )}
              </div>              
              <button
                className="btn btn-outline-secondary btn-sm w-100"
                onClick={() => onDebug && onDebug(property)}
                title="View room debug information"
              >
                <i className="fas fa-bug me-1"></i>
                Debug
              </button>
            </div>
          </div>
          
          {/* Availability */}
          <div className="mt-2">
            <small className="text-success">
              <i className="fas fa-calendar-check me-1"></i>
              Available from {new Date(property.availability.move_in_date).toLocaleDateString()}
            </small>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        .hover-card {
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
        }
        
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
        }
        
        .text-small {
          font-size: 0.75rem;
        }
      `}</style>
    </div>
  );
};

export default PropertyCard;